import { useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import Checkbox from '@mui/material/Checkbox'
import { concatMap, first, iif } from 'rxjs'
import { useDispatch } from 'react-redux'
import { scrollTo } from 'libraries/front-end-core/helpers/document'
import { DOCUMENTS_COPY_BUTTON, DOCUMENTS_FAVOURITING } from 'constants/feature-flags'
import { Document } from 'services/DocumentService'
import { setSelectedDocuments } from 'redux/actions'
import { FAVOURITES } from 'constants/requests'
import { refresh } from 'services/ApiService'
import { addUserFavourite, removeUserFavourite } from 'services/FavouritesService'
import FavouriteButton from 'components/shared/FavouriteButton'
import SuiteLink from './SuiteLink'
import DownloadLink from './DownloadLink'
import CopyLink from './CopyLink'

interface DocumentItemProps extends Document {
  selectedDocuments: Document[]
  aol: string
  focusedId?: string | null
  pageId: string
  selectable: boolean
  expanded: boolean
  rootId?: string
  favourites?: Set<string>
}

export default function DocumentItem ({
  selectedDocuments,
  id,
  title,
  description,
  focusedId,
  automated,
  aol,
  clarilis,
  cta,
  position,
  parentId,
  hasAccess,
  underMaintenance,
  pageId,
  selectable,
  expanded,
  rootId,
  favourites
}: DocumentItemProps): JSX.Element {
  const dispatch = useDispatch()
  const selected = selectedDocuments.some(selectedDocument => selectedDocument.id === id)
  const isFavourite = favourites?.has(id) ?? false

  const handleFavouriteClick = (e: React.MouseEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    iif(
      () => isFavourite,
      removeUserFavourite(id, 'documents'),
      addUserFavourite(id, 'documents')
    ).pipe(
      concatMap(() => refresh(FAVOURITES)),
      first()
    ).subscribe()
  }

  const elementId = `document-${id}`
  const [isDescExpanded, setIsDescExpanded] = useState(false)
  const [isClamped, setIsClamped] = useState(false)
  const descRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (expanded && focusedId === id) {
      setTimeout(() => {
        scrollTo({ pageId, id: elementId, animate: true })
      }, 0)
    }
  }, [expanded, focusedId, id, pageId, elementId])

  useEffect(() => {
    const el = descRef.current
    if ((el == null) || isDescExpanded) return

    const checkClamped = (): void => setIsClamped(el.scrollHeight > el.clientHeight)
    checkClamped()

    const ro = new ResizeObserver(checkClamped)
    ro.observe(el)
    return () => ro.disconnect()
  }, [isDescExpanded])

  const titleElement = <h3 className='ma0 f5 w-100-m mb3-m' dangerouslySetInnerHTML={{ __html: title }} />

  const copyLinkElement = DOCUMENTS_COPY_BUTTON && rootId !== undefined && (
    <CopyLink rootId={rootId} documentId={id} />
  )

  const favouriteElement = DOCUMENTS_FAVOURITING && (
    <FavouriteButton className='ml5 ml0-m' isFavourite={isFavourite} onClick={handleFavouriteClick} />
  )

  const hasLinkFavouriteGroup = (DOCUMENTS_COPY_BUTTON && rootId !== undefined) || DOCUMENTS_FAVOURITING

  const badgeElement = automated && (
    <div className='br2 bg-slate-grey white ph3 pv0-half f7 ml5 ml0-m mt3-m order-3-m'>
      Automated
    </div>
  )

  const descriptionElement = description !== null && description !== '' && (
    <div className='mv5 basis-100 order-4-m'>
      <div
        ref={descRef}
        className={cx({ 'description-line-clamp': !isDescExpanded })}
      >
        <p className='ma0' dangerouslySetInnerHTML={{ __html: description }} />
      </div>
      {isClamped && (
        <button
          type='button'
          className='mt2 db pointer bn bg-transparent pa0 underline black'
          onClick={() => setIsDescExpanded(prev => !prev)}
        >
          {isDescExpanded ? 'See less' : 'See more'}
        </button>
      )}
    </div>
  )

  const actionIconElement = automated
    ? <SuiteLink aol={aol} id={id} clarilis={clarilis} cta={cta} selectable={selectable} />
    : <DownloadLink id={id} aol={aol} selectable={selectable} />

  const checkboxElement = selectable && (
    <Checkbox
      className='mlauto-m'
      checked={selected}
      onChange={() => {
        dispatch(
          setSelectedDocuments(
            selected
              ? selectedDocuments.filter(selectedDocument => selectedDocument.id !== id)
              : [...selectedDocuments, { id, title, description, automated, clarilis, cta, parentId, hasAccess, underMaintenance, position }]
          )
        )
      }}
      inputProps={{ 'aria-label': selected ? 'Deselect document' : 'Select document' }}
    />
  )

  const liClassName = cx(
    'bb bb-light-bluish-grey ph6 pv5 flex items-center justify-between flex-wrap-m justify-start-m',
    {
      'highlight-yellow': !selected && focusedId === id,
      'bg-precedents-blue-20': selected
    }
  )

  return (
    <li id={elementId} className={liClassName}>
      <div className='flex flex-wrap mr10 items-center contents-m'>
        {titleElement}
        {hasLinkFavouriteGroup && (
          <div className='flex items-center gap8-m'>
            {copyLinkElement}
            {favouriteElement}
          </div>
        )}
        {badgeElement}
        {automated && <div className='w-100-m order-2-m' />}
        {descriptionElement}
      </div>
      <div className={cx('flex items-center mlauto gap8-m grow-1-m order-1-m', { 'ml8-m': hasLinkFavouriteGroup, 'ml0-m': !hasLinkFavouriteGroup })}>
        {actionIconElement}
        {checkboxElement}
      </div>
    </li>
  )
}
